import React, { useCallback, useEffect } from 'react';
import { strings } from 'src/localization';
import { ComponentMapIds } from 'src/components/map';
import { IView } from 'Framework';
import css from './ThankYou.module.scss';
import { IconCheckMark } from 'src/content/icons';
import Footer from 'src/components/Footer/Footer';
import { Customer, UpdateApptDateInp, SpecData, ApptVeh, UpdateApptCustInp, DepartmentSpec, UpdateApptOpsInp, UpdateApptVehInp } from 'src/models';
// import { DateTime } from '@reynolds/datetime';
import { ViewMode } from 'src/enums';
import { ScrollBar } from '@framework-controls/core';
import { IAppointmentState } from 'src/store/states';
import classNames from 'classnames';

export interface IProps {
    custInfo?: Customer;
    appointment: IAppointmentState;
    vehicle: ApptVeh;
    viewMode: ViewMode;
    specData?: SpecData;
    theme: string;
    specDeptData: DepartmentSpec;
    selectVehicle: (id: string) => void;
    onAppCustUpdate: (apptCust: UpdateApptCustInp) => void;
    pushToMainPanes: (inputView: IView) => void;
    clearMainPanes: () => void;
    clearCustInfo: () => void;
    clearNewVehicle: () => void;
    clearVehicles: () => void;
    clearApptState: () => void;
    clearROState: () => void;
    clearROHistory: () => void;
    clearMenusState: () => void;
    setViewMode: (viewMode: ViewMode) => void;
    updateApptDate: (inp: UpdateApptDateInp) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    updateOps: (inputOps: UpdateApptOpsInp[]) => void;
    updateApptVehicle: (inp: UpdateApptVehInp) => void;
}

const ThankYou = (props: IProps) => {
    // On No Thanks take the user to the Landing Page.  If the were not signed in.
    const noThanksHandler = useCallback(() => {
        props.clearMainPanes();
        props.clearApptState();
        props.clearCustInfo();
        props.clearMenusState();
        props.clearROHistory();
        props.clearVehicles();
        props.clearNewVehicle();
        props.setViewMode(ViewMode.LandingPage);
    }, [props?.clearApptState, props?.clearCustInfo, props?.clearMainPanes, props?.clearMenusState,
        props?.clearNewVehicle, props?.clearROHistory, props?.clearVehicles, props?.setViewMode]);
    // If the user was signed in, No Thanks will take the user back to their Customer Account.
    // We want the vehcile from the appointment to be selected.
    const viewAcctHandler = useCallback(() => {
        props.clearMainPanes();
        props.clearApptState();
        props.clearMenusState();
        props.clearNewVehicle();
        props.clearROHistory();
        props.setViewMode(ViewMode.CustAcct);
        // Need to update the customer state with the new appointment data
        const apptCustInp: UpdateApptCustInp = {
            nameId: props.custInfo!.nameId,
            firstName: props.appointment.ApptCust.custFirstName,
            lastName: props.appointment.ApptCust.custLastName,
            emailAddress: props.appointment.ApptCust.custEmailAddress,
            phoneNumber: props.appointment.ApptCust.custPhoneNumber,
            phoneType: props.custInfo!.phoneType,
            prefContact: props.appointment.ApptCust.custPrefContact,
            loggedIn: props.custInfo!.login,
        };
        props.onAppCustUpdate(apptCustInp);
        props.pushToMainPanes({
            viewId: ComponentMapIds.UserAccountMenu,
            component: ComponentMapIds.UserAccountMenu,
        });
    }, [props?.appointment, props?.clearApptState, props?.clearMainPanes,
        props?.clearMenusState, props?.clearNewVehicle, props?.clearROHistory, props?.custInfo,
        props?.onAppCustUpdate, props?.pushToMainPanes, props?.setViewMode]);

    //  If signed in, and the user pressed Add Appointment, take them to the
    //  Veh Select screen.  Make sure to keep the cusotmer info in the appointment state.
    const addApptLoggedInHandler = useCallback(() => {
        props.clearMainPanes();
        props.clearApptState();
        props.clearMenusState();
        props.clearROHistory();
        props.clearNewVehicle();
        props.setViewMode(ViewMode.Appt);
        // Need to update the customer state with the new appointment data
        const apptCustInp: UpdateApptCustInp = {
            nameId: props.custInfo!.nameId,
            firstName: props.appointment.ApptCust.custFirstName,
            lastName: props.appointment.ApptCust.custLastName,
            emailAddress: props.appointment.ApptCust.custEmailAddress,
            phoneNumber: props.appointment.ApptCust.custPhoneNumber,
            phoneType: props.custInfo!.phoneType,
            prefContact: props.appointment.ApptCust.custPrefContact,
            loggedIn: props.custInfo!.login,
        };
        props.onAppCustUpdate(apptCustInp);
        props.pushToMainPanes({
            viewId: ComponentMapIds.LandingPage,
            component: ComponentMapIds.LandingPage,
        });
        props.pushToMainPanes({
            viewId: ComponentMapIds.VehSel,
            component: ComponentMapIds.VehSel,
        });
    }, [props?.clearApptState, props?.clearMainPanes, props?.clearMenusState, props.setViewMode, props.appointment,
        props?.clearNewVehicle, props?.clearROHistory, props?.pushToMainPanes, props.custInfo, props.onAppCustUpdate]);

    const brandFlags = [
        props.specData?.fordSmarttFlag,
        props.specData?.kiaTrackingFlag,
        // Add more brand tracking flags here in the future
    ];
    const hasAnyBrandFlag = brandFlags.some(Boolean);
    //  If NOT signed in and the user selected Add Appointment take them to the start of
    //  The Add Appointment process.
    const addApptHandler = () => {
        props.clearMainPanes();
        // props.clearApptState();
        // props.clearCustInfo();
        const dateInp: UpdateApptDateInp = {
            selDate: '',
            selTime: '',
            selAdv: '',
            selTrans: '',
            selAdvName: '',
            selAdvImage: '',
            selTransFlag: '',
            selApptNum: '',
        };
        props.updateApptDate(dateInp);
        const vehInp: UpdateApptVehInp = {
            vin: '',
            makeCode: '',
            make: '',
            model: '',
            series: '',
            vehYr: '',
            vehColor: '',
            mileage: 0,
            imagePath: '',
        };
        props.updateApptVehicle(vehInp);
        props.updateOps([]);
        props.clearMenusState();
        props.clearROHistory();
        props.clearVehicles();
        props.clearNewVehicle();
        props.setViewMode(ViewMode.Appt);
        if (!hasAnyBrandFlag) {
            props.pushToMainPanes({
                viewId: ComponentMapIds.LandingPage,
                component: ComponentMapIds.LandingPage,
            });
        }
        props.pushToMainPanes({
            viewId: ComponentMapIds.VehSel,
            component: ComponentMapIds.VehSel,
        });
    };

    let footer = props.custInfo !== undefined && props.custInfo.hasAccount ?
        <div>
            <Footer
                id='ThankYouFooter'
                message={strings.AddAppt}
                handler={addApptLoggedInHandler}
                isDisabled={false}
                secondaryMessage={strings.NoThanks}
                secondaryhandler={viewAcctHandler}
            />
        </div> :
        <div>
            <Footer
                id='ThankYouFooter'
                message={strings.AddAppt}
                handler={addApptHandler}
                isDisabled={false}
                secondaryMessage={strings.NoThanks}
                secondaryhandler={noThanksHandler}
            />
        </div>;

    if (hasAnyBrandFlag) {
        footer = <div>
            <Footer
                id='ThankYouFooter'
                message={strings.AddAppt}
                handler={addApptHandler}
                isDisabled={false}
                secondaryhandler={() => null}
            />
        </div>;
    }

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'ThankYou', '');
    }, [
        props.addLogEntry,
    ]);

    const congratsBarClass = classNames(
        css.congratsBar,
        {
            [css.congratsBarAcura]: props.theme === 'acura',
        },
    );

    return (
        <div>
            <ScrollBar scrollVertical>

                <div className={css.confirmation}>
                    <div className={css.congratsBox}>
                        <div className={css.congratsText}>
                            <div className={css.iconBorder}>
                                <IconCheckMark
                                    className={css.check}
                                />
                            </div>
                            <h2 className={css.congratsLit} >{strings.ThankYou}</h2>
                        </div>
                        <hr className={congratsBarClass} />
                        <div>{strings.ApptCanceled}</div>
                    </div>
                    <div className={css.createBox}>
                        {strings.CreateNewAppt}
                    </div>
                </div>
            </ScrollBar>
            {footer}
        </div >
    );
};

export default ThankYou;